import {createContext, type PropsWithChildren, use, useCallback, useMemo, useState} from "react";
import {AuthorizationStatus, getMessaging} from "@react-native-firebase/messaging";
import {FCMTokenRegisterRequest} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {useActivity} from "@/lib/components/ActivityProvider";
import {PermissionsAndroid, Platform} from "react-native";
import * as Device from "expo-device";

export enum NotificationStatus {
    UNKNOWN,
    BUSY,
    ACTIVE,
    ERROR,
    DENIED,
    UNAVAILABLE,
}

const NotificationContext = createContext<{
    notificationStatus: NotificationStatus,
    acquireNotificationToken: () => any,
}>({
    notificationStatus: NotificationStatus.UNKNOWN,
    acquireNotificationToken: () => {
    },
});

export function useNotification() {
    const value = use(NotificationContext);
    if (!value) {
        throw new Error('useNotifications must be wrapped in a <NotificationProvider />');
    }
    return value;
}

// Google Firebase Cloud Messaging
export function NotificationProvider({children}: PropsWithChildren) {
    const {session} = useSession();
    const {refreshActivities} = useActivity();

    const [notificationStatus, setNotificationStatus] = useState<NotificationStatus>(NotificationStatus.UNKNOWN);
    const [hasNotificationHandler, setHasNotificationHandler] = useState(false);

    const messaging = useMemo(() => {
        try {
            return getMessaging();
        } catch {
            setNotificationStatus(NotificationStatus.UNAVAILABLE);
        }
    }, []);

    const askPermission = useCallback(async () => {
        if (!messaging) {
            throw new Error(`Messaging unavailable`);
        }
        if (Platform.OS === "android") {
            const status = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS);
            const enabled = status === "granted" || status === "never_ask_again";
            if (!enabled) {
                throw new Error(`Android permission request said: ${status}`);
            }
        } else if (Platform.OS === "ios") {
            const permissionsStatus = await messaging.requestPermission();
            const enabled =
                permissionsStatus === AuthorizationStatus.AUTHORIZED ||
                permissionsStatus === AuthorizationStatus.PROVISIONAL;
            if (!enabled) {
                throw new Error(`iOS permission request said: ${permissionsStatus}`);
            }
        }
    }, [messaging]);

    const acquireNotificationToken = useCallback(async () => {
        if (!messaging || !session || (!Device.isDevice && Platform.OS === "ios") || Platform.OS === "web") {
            setNotificationStatus(NotificationStatus.UNAVAILABLE);
            return;
        }
        try {
            setNotificationStatus(NotificationStatus.BUSY);
            await askPermission();
            const token = await messaging.getToken();
            const payload: FCMTokenRegisterRequest = {token};
            await fetchBackendNoContent(`/account/fcm`, "PUT", session, payload);
            setNotificationStatus(NotificationStatus.ACTIVE);
        } catch {
            setNotificationStatus(NotificationStatus.ERROR);
        }
    }, [messaging, session, askPermission]);

    if (messaging && !hasNotificationHandler) {
        setHasNotificationHandler(true);
        console.log("Setting up in-app notification handler")
        messaging.onMessage((msg) => {
            console.log("Notification received in-app", msg);
            refreshActivities();
        });
        // console.log("Check initial notification")
        // messaging.getInitialNotification().then((msg) => {
        //     console.log("Initial notification", msg);
        // });
        // console.log("Setting up remote notification handler")
        // messaging.onNotificationOpenedApp((msg)=> {
        //     console.log("Remote FCM message", msg)
        // })
    }

    return (
        <NotificationContext.Provider
            value={{
                notificationStatus,
                acquireNotificationToken,
            }}>
            {children}
        </NotificationContext.Provider>
    );
}